import { z } from 'zod';
import { entrySource, id, isoDateTime, percent } from './common.js';
import { recordFormatKey } from './record-formats.js';
/**
 * The classic seven-column CBT thought record: situation, emotions, automatic
 * thought, evidence for, evidence against, balanced response, re-rating.
 *
 * The re-rating is the part that does the work. A record without `beliefAfter`
 * and post-emotion intensities is an unfinished record — the user has written
 * down the distressing thought and then walked away from it, which can leave
 * them worse off than not starting. The API tracks completeness explicitly so
 * the UI can nudge these to a close.
 */
export const emotionEntry = z.object({
    emotion: z.string().min(1).max(40),
    intensityBefore: percent,
    intensityAfter: percent.nullable().default(null),
});
export const evidenceSide = z.enum(['for', 'against']);
export const evidenceEntry = z.object({
    side: evidenceSide,
    text: z.string().min(1).max(2000),
});
export const createThoughtRecord = z.object({
    occurredAt: isoDateTime.optional(),
    /** Which worksheet produced this. Only affects how it is labelled when read back. */
    format: recordFormatKey.default('standard'),
    situation: z.string().min(1).max(4000),
    automaticThought: z.string().min(1).max(4000),
    beliefBefore: percent.nullable().default(null),
    beliefAfter: percent.nullable().default(null),
    emotions: z.array(emotionEntry).max(12).default([]),
    evidence: z.array(evidenceEntry).max(40).default([]),
    /** Distortion keys from the seeded reference table, e.g. `mind_reading`. */
    distortions: z.array(z.string().min(1).max(60)).max(20).default([]),
    /**
     * Which of `distortions` the app picked, not him: set only by a worksheet
     * drafted from a journal entry (source 'ai_assisted'), and ignored on any
     * other. Optional, so every other caller is unchanged.
     */
    appDistortions: z.array(z.string().min(1).max(60)).max(20).optional(),
    response: z.string().max(4000).nullable().default(null),
    outcomeNote: z.string().max(2000).nullable().default(null),
    source: entrySource.default('manual'),
    voiceLogId: id.nullable().default(null),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
});
export const updateThoughtRecord = createThoughtRecord.partial();
/**
 * Payload for turning a spoken ramble into a structured draft. The user talks
 * for two minutes; the model sorts it into the seven columns. The result is
 * always returned as a *draft* for review, never written straight to the
 * record — it is the user's account of their own experience, and a model
 * quietly rewording it would defeat the point of the exercise.
 */
export const assistThoughtRecord = z.object({
    transcript: z.string().min(1).max(20000),
    voiceLogId: id.optional(),
});
/**
 * The steps a drafted worksheet's question can sit on.
 *
 * The model's questions were a list on the first screen, a paragraph of prose
 * about things four screens away, and two of them asked for numbers (13 Sep).
 * Each now names its step and shows there. Never the answer step: that one is
 * his, and a question sitting on it would be the app leaning on his answer.
 */
export const DRAFT_GAP_STEPS = ['situation', 'emotions', 'thought', 'evidenceFor', 'evidenceAgainst'];
export const draftGapStep = z.enum(DRAFT_GAP_STEPS);
/**
 * A question asking for a 0-100 (or out-of-10) number.
 *
 * The worksheet asks for those itself, beside the slider, so a model question
 * for one asks twice — once in prose and once with the control. Dropped on the
 * server for a fresh draft, and on the worksheet for one still in the browser's
 * history from before.
 */
export function asksForNumber(question, feelings = []) {
    // A scale, written out: "0-100", "1 to 10", "on a scale", "out of 10".
    if (/\b[01]\s*(?:-|–|—|to)\s*10{1,2}\b|\bon a scale\b|\bout of 10{1,2}\b(?!\s+[a-z])/i.test(question))
        return true;
    const words = feelings
        .map((w) => w.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'))
        .filter(Boolean);
    const feeling = words.length > 0 ? new RegExp(`\\b(?:${words.join('|')})\\b`, 'i') : null;
    // One clause at a time, so "how strong is the evidence…, and what do you
    // believe…" is two questions, neither of them a rating.
    return question.split(/[,;?.!]/).some((clause) => {
        if (/\bhow (?:much|strongly) (?:do|did) you (?:still )?believe\b/i.test(clause))
            return true;
        const strength = /\bhow (?:strong|strongly|intense|intensely|bad)\b/i.test(clause);
        if (strength && (/\bfe(?:el|lt|eling|elings)\b/i.test(clause) || feeling?.test(clause)))
            return true;
        // "How stressed were you?" — how, then one of the feelings he named.
        return feeling !== null && new RegExp(`\\bhow\\s+(?:${words.join('|')})\\b`, 'i').test(clause);
    });
}
/**
 * Whether a feeling has a strength he gave, by the same check a saved worksheet
 * makes (emotionEntry's `percent`).
 *
 * A drafted feeling he named but never scored has none. It used to be given 60
 * — the slider's starting place — and saved as if he had said 60. Now it reads
 * "Not said yet" and is saved only once it has a strength. The save filter and
 * every sentence saying an unrated feeling won't be kept call this, so the copy
 * cannot drift from the rule.
 */
export function hasStrength(e) {
    return emotionEntry.shape.intensityBefore.safeParse(e.intensityBefore).success;
}
//# sourceMappingURL=thought-record.js.map